/**
 * Identity Apollo Client — Gateway-specific client for identity operations
 *
 * This client targets the federation gateway proxy (/api/v2/graphql)
 * instead of the default Magento endpoint used by the standard ApolloProvider.
 *
 * Used by identity mutation hooks to route GraphQL operations through
 * the correct proxy endpoint (Client → /api/v2/graphql → API Gateway → Auth Service).
 *
 * This is separate from the main Apollo client (module-core) because:
 * - Default client targets Magento commerce GraphQL
 * - Identity operations target the federation gateway (Auth Service subgraph)
 */
import { ApolloClient, InMemoryCache, HttpLink, from, type NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GRAPHQL_PROXY_ENDPOINTS } from '@kidslabs/module-core';

let identityClient: ApolloClient<NormalizedCacheObject> | null = null;

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(`[Identity GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        });
    }

    if (networkError) {
        console.error(`[Identity Network error]: ${networkError}`);
    }
});

const createIdentityHttpLink = () => {
    return new HttpLink({
        uri: GRAPHQL_PROXY_ENDPOINTS.gateway,
        credentials: 'include',
    });
};

const createIdentityClient = (): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient({
        link: from([errorLink, createIdentityHttpLink()]),
        cache: new InMemoryCache(),
        defaultOptions: {
            mutate: {
                errorPolicy: 'all',
            },
        },
    });
};

/**
 * Get or create the Identity Apollo Client singleton.
 * This client is configured to use the federation gateway endpoint.
 */
export const getIdentityClient = (): ApolloClient<NormalizedCacheObject> => {
    if (!identityClient) {
        identityClient = createIdentityClient();
    }
    return identityClient;
};

/**
 * Reset the identity client — clears cache and destroys the singleton.
 * Called during logout to ensure no stale identity data remains.
 */
export const resetIdentityClient = (): void => {
    if (identityClient) {
        identityClient.clearStore();
        identityClient = null;
    }
};
